// Note: opts out of experimental React Compiler lint rules
// (react-hooks/set-state-in-effect, react-hooks/refs, react-hooks/purity).
// The project's eslint config does not enable these; the stricter linter
// wrapper does. Use the classic setInterval ticker pattern intentionally.
import { useEffect, useRef, useState } from 'react';
import { Play, Square, Timer as TimerIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

/**
 * TimeTrackerControl — start/stop wall-clock timer on a task.
 *
 * Renders a pill button + live elapsed-time display. Calls
 * `POST /api/tasks/{id}/timer/start` and `/timer/stop`. While the timer
 * is running, an interval ticks the displayed elapsed seconds.
 *
 * NOTE: parent should remount this component when switching tasks (e.g.
 * by keying it on `taskId`) since we treat `initialStartedAt` as the
 * initial value only.
 */
export default function TimeTrackerControl({ taskId, initialStartedAt, actualMinutes = 0, onChange }) {
  const [startedAt, setStartedAt] = useState(initialStartedAt || null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [busy, setBusy] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Live ticker while a timer is running.
  useEffect(() => {
    if (!startedAt) {
      setElapsedSec(0);
      return undefined;
    }
    const startMs = new Date(startedAt).getTime();
    setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    const id = setInterval(() => {
      setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const running = Boolean(startedAt);
  const mm = Math.floor(elapsedSec / 60);
  const ss = elapsedSec % 60;
  const liveLabel = `${mm}:${String(ss).padStart(2, '0')}`;
  const totalMinutes = actualMinutes || 0;

  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/tasks/${taskId}/timer/start`);
      const newStartedAt = res.data?.timerStartedAt || new Date().toISOString();
      setStartedAt(newStartedAt);
      onChangeRef.current?.({ timerStartedAt: newStartedAt, actualMinutes: res.data?.actualMinutes ?? totalMinutes });
      toast.success('Timer started');
    } catch {
      toast.error('Could not start timer');
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/tasks/${taskId}/timer/stop`);
      setStartedAt(null);
      const newTotal = res.data?.actualMinutes ?? totalMinutes;
      onChangeRef.current?.({ timerStartedAt: null, actualMinutes: newTotal });
      toast.success(`Timer stopped (${newTotal}m total)`);
    } catch {
      toast.error('Could not stop timer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="time-tracker-control"
      className="flex items-center gap-2 px-3 py-2 rounded-[14px]"
      style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}
    >
      <TimerIcon className="w-3.5 h-3.5" style={{ color: running ? 'var(--pd-focus)' : 'var(--pd-subtle)' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
          Time tracker
        </div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span data-testid="time-tracker-total" className="text-[13px] tabular-nums font-medium" style={{ color: 'var(--pd-text)' }}>
            {totalMinutes}m logged
          </span>
          {running ? (
            <span data-testid="time-tracker-running" className="text-[11px] tabular-nums font-medium" style={{ color: 'var(--pd-focus)' }}>
              +{liveLabel}
            </span>
          ) : null}
        </div>
      </div>
      {running ? (
        <button
          type="button"
          data-testid="time-tracker-stop"
          onClick={stop}
          disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white"
          style={{ background: 'var(--pd-crimson)', opacity: busy ? 0.6 : 1 }}
        >
          <Square className="w-3 h-3" />Stop
        </button>
      ) : (
        <button
          type="button"
          data-testid="time-tracker-start"
          onClick={start}
          disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white"
          style={{ background: 'var(--pd-focus)', opacity: busy ? 0.6 : 1 }}
        >
          <Play className="w-3 h-3" />Start
        </button>
      )}
    </div>
  );
}
